import React, { useState, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import Button from '../../../../../ui/canon/Button';
import Input from '../../../../../ui/canon/Input';
import { SendIcon, SparklesIcon, HelpCircleIcon, CheckIcon } from 'lucide-react';

const DEFAULT_MAX_LENGTH = 1000;

// Counter for generating unique IDs (works with React 16+)
let idCounter = 0;
const generateId = () => `text-input-widget-${++idCounter}`;

const TextInputWidget = ({
  onAnswer,
  disabled,
  placeholder = 'Type your answer...',
  multiline = true,
  onHelp,
  aiSuggestion,
  initialValue = '',
  required = false,
  maxLength = DEFAULT_MAX_LENGTH,
  helpText,
}) => {
  const intl = useIntl();
  const [value, setValue] = useState(initialValue || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuggestion, setShowSuggestion] = useState(!!aiSuggestion);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const [error, setError] = useState('');
  const [touched, setTouched] = useState(false);

  // Generate unique ID for accessibility (compatible with React 16+)
  const idRef = useRef(null);
  if (!idRef.current) {
    idRef.current = generateId();
  }
  const inputId = idRef.current;

  useEffect(() => {
    setValue(initialValue || '');
    setIsSubmitting(false);
    setError('');
    setTouched(false);
  }, [initialValue]);

  // Validate the input value
  const validate = (val) => {
    if (required && !val.trim()) {
      return intl.formatMessage({ id: 'succession.plans.widget_text_required' });
    }
    return '';
  };

  const handleSubmit = () => {
    const validationError = validate(value);
    if (validationError) {
      setError(validationError);
      setTouched(true);
      return;
    }

    if (value.trim() && !isSubmitting) {
      setIsSubmitting(true);
      // Ensure value is always sent as an object matching backend expectations
      onAnswer({ value: value.trim() });
    }
  };

  const handleChange = (e) => {
    let newValue = e.target.value;

    // Enforce maxLength - prevent input beyond limit
    if (maxLength && newValue.length > maxLength) {
      newValue = newValue.slice(0, maxLength);
    }

    setValue(newValue);

    // Clear error if user starts typing after validation error
    if (error && newValue.trim()) {
      setError('');
    }
  };

  const handleBlur = () => {
    setTouched(true);
    if (required && !value.trim()) {
      setError(intl.formatMessage({ id: 'succession.plans.widget_text_required' }));
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && !multiline) {
      e.preventDefault();
      handleSubmit();
    }
  };

  // Character counter values
  const currentLength = value.length;
  const isAtLimit = maxLength && currentLength >= maxLength;

  const handleUseSuggestion = () => {
    if (aiSuggestion) {
      setValue(aiSuggestion);
      setIsFadingOut(true);
      setTimeout(() => {
        setShowSuggestion(false);
        setIsFadingOut(false);
      }, 300);
    }
  };

  const textareaBaseClasses = 'w-full bg-succession-bg-primary text-succession-text-primary placeholder-succession-text-muted border hover:border-succession-border-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-succession-focus-ring rounded-md transition-theme duration-theme text-sm px-3 py-1.5 min-h-[100px] resize-none';
  const textareaClasses = `${textareaBaseClasses} ${error ? 'border-red-500' : 'border-succession-border'}`;

  return (
    <div className="space-y-3">
      {/* AI Helper Button */}
      {onHelp && (
        <div className="flex gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={onHelp}
            disabled={disabled || isSubmitting}
            className="gap-2"
          >
            <HelpCircleIcon className="h-3.5 w-3.5" />
            {intl.formatMessage({ id: 'succession.plans.widget_help_button' })}
          </Button>
        </div>
      )}

      {/* AI Suggestion */}
      {showSuggestion && aiSuggestion && (
        <div
          className={`bg-succession-primary/5 border border-succession-primary/20 rounded-lg p-4 space-y-3 transition-opacity duration-300 ${
            isFadingOut ? 'opacity-0' : 'opacity-100'
          }`}
        >
          <div className="flex items-start gap-3">
            <SparklesIcon className="h-4 w-4 text-succession-primary flex-shrink-0 mt-0.5" />

            <div className="flex-1 space-y-2">
              <p className="text-sm font-medium text-succession-text-primary">
                {intl.formatMessage({ id: 'succession.plans.ai_suggestion_label' })}
              </p>
              <p className="text-sm text-succession-text-secondary">{aiSuggestion}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="primary"
              size="sm"
              onClick={handleUseSuggestion}
              className="gap-2"
            >
              <CheckIcon className="h-3.5 w-3.5" />
              {intl.formatMessage({ id: 'succession.plans.ai_suggestion_use' })}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowSuggestion(false)}
            >
              {intl.formatMessage({ id: 'succession.plans.ai_suggestion_dismiss' })}
            </Button>
          </div>
        </div>
      )}

      {/* Input Field */}
      <div>
        {multiline ? (
          <textarea
            id={inputId}
            value={value}
            onChange={handleChange}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled || isSubmitting}
            className={textareaClasses}
            aria-invalid={!!error}
            aria-describedby={`${inputId}-help ${error ? `${inputId}-error` : ''}`.trim()}
          />
        ) : (
          <Input
            id={inputId}
            type="text"
            value={value}
            onChange={handleChange}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled || isSubmitting}
            variant={error ? 'danger' : 'primary'}
            aria-invalid={!!error}
            aria-describedby={`${inputId}-help ${error ? `${inputId}-error` : ''}`.trim()}
          />
        )}

        {/* Helper text, error message, and character counter row */}
        <div className="mt-1.5 flex justify-between items-start">
          <div className="flex-1">
            {/* Error Message */}
            {error && touched && (
              <p
                id={`${inputId}-error`}
                className="text-sm text-red-500"
                role="alert"
              >
                {error}
              </p>
            )}

            {/* Help Text */}
            {helpText && !error && (
              <p id={`${inputId}-help`} className="text-xs text-succession-text-muted">
                {helpText}
              </p>
            )}
          </div>

          {/* Character Counter */}
          {maxLength && (
            <p
              className={`text-xs flex-shrink-0 ml-2 ${
                isAtLimit ? 'text-amber-500 font-medium' : 'text-succession-text-muted'
              }`}
              aria-live="polite"
              aria-atomic="true"
            >
              {intl.formatMessage({ id: 'succession.plans.widget_text_char_count' }, { current: currentLength, max: maxLength })}
            </p>
          )}
        </div>
      </div>

      <Button
        onClick={handleSubmit}
        disabled={!value.trim() || disabled || isSubmitting}
        className="w-full"
      >
        <SendIcon className="h-4 w-4 mr-2" />
        {intl.formatMessage({ id: 'succession.plans.widget_submit_answer' })}
      </Button>
    </div>
  );
};

export default TextInputWidget;
